import { useSelector } from "react-redux";
import { Formik, Form as FormikForm } from "formik";
import { Grid, Button, Icon, Divider, Checkbox, Message, Dropdown } from "semantic-ui-react";
import { ErrorInfo } from "components/form";
import {
  formatMediaDeliveryPersonContacts,
  formatPrintSizes,
  formatResolutions,
} from "components/CampaignInfo/CampaignInfo";
import { useEffect, useMemo, useState } from "react";
import _, { values } from "lodash";
import { formatDate, getAWSDate } from "services/common";
import { getActiveUser, getAgencys, getGroupedCampaignById, getUsers } from "store/selectors";
import { AgencyType, CampaignType, CampaignStatus } from "models";
import {
  getCampaignUpdatePrintSizeChanges,
  getCampaignUpdateResolutionLists,
  makeDigitalSizesList,
} from "./Campaign";
import { toast } from "components/Toast/Toast";
import AssetSelectionCarousel from "components/AssetSelectorCarousel/AssetsSelectorCarousel";
import { MergeCampaignPreviewModal } from "components/MergeCampaignPreviewModal/MergeCampaignPreviewModal";
import LoadResolver from "components/LoadResolver";
import { useTranslation } from "react-i18next";

const CAMPAIGN_KEYS = [
  "name",
  "advertiser",
  "contract_number",
  "start_time",
  "end_time",
  "deliveryDeadline",
  "notes",
  "adminNotes",
  "scheduling_notes",
  "printSizes",
  "requiredResolutions",
  "orderlineID",
  "salesperson",
  "salesperson_contact",
  "mediaDeliveryPersonContacts",
];

const CAMPAIGN_FIELDS_TO_COMBINE = [
  "notes",
  "adminNotes",
  "scheduling_notes",
  "mediaDeliveryPersonContacts",
  "orderlineID",
];

export const MergeCampaignsForm = (props) => {
  const { campaignsToBeMerged, onSuccessRunQC, onSuccessRedirect, goPrev, dispatch, t } = props;

  useEffect(() => {
    const firstCampaign = campaignsToBeMerged[0] && campaignsToBeMerged[0];
    const secondCampaign = campaignsToBeMerged[1] && campaignsToBeMerged[1];
    if (firstCampaign && secondCampaign) {
      dispatch.campaigns.loadOne(firstCampaign.id);
      dispatch.campaigns.loadOne(secondCampaign.id);
      dispatch.assets.loadCampaignAssets(firstCampaign.id);
      dispatch.assets.loadCampaignAssets(secondCampaign.id);
    }
  }, [campaignsToBeMerged]);

  const firstGroupedCampaign = useSelector((state) =>
    getGroupedCampaignById(state, {
      campaignId: campaignsToBeMerged[0] && campaignsToBeMerged[0] && campaignsToBeMerged[0].id,
    })
  );

  const secondGroupedCampaign = useSelector((state) =>
    getGroupedCampaignById(state, {
      campaignId: campaignsToBeMerged[1] && campaignsToBeMerged[1].id,
    })
  );

  useEffect(() => {
    if (firstGroupedCampaign && secondGroupedCampaign) {
      setFirstCampaignAndAssets(firstGroupedCampaign);
      setSecondCampaignAndAssets(secondGroupedCampaign);
    }
  }, [firstGroupedCampaign, secondGroupedCampaign]);

  const [firstCampaignAndAssets, setFirstCampaignAndAssets] = useState(campaignsToBeMerged[0]);
  const [secondCampaignAndAssets, setSecondCampaignAndAssets] = useState(campaignsToBeMerged[1]);

  const campaignHasProcessedAssets = () => {
    const { campaign, processedAssets } = firstCampaignAndAssets;
    return campaign && processedAssets.length > 0 && campaign.type === CampaignType.DIGITAL;
  };

  const firstCampaign = useMemo(() => firstCampaignAndAssets.campaign, [firstCampaignAndAssets]);
  const secondCampaign = useMemo(() => secondCampaignAndAssets.campaign, [secondCampaignAndAssets]);

  const firstCampaignAssets = useMemo(() => firstCampaignAndAssets.assets, [
    firstCampaignAndAssets,
  ]);
  const secondCampaignAssets = useMemo(() => secondCampaignAndAssets.assets, [
    secondCampaignAndAssets,
  ]);

  // First campaign assets' resolutions for both types mp4 and jpeg
  const firstCampaignDeliveredAssetResolutions = useMemo(
    () =>
      firstCampaign && firstCampaign.type === CampaignType.DIGITAL
        ? _.map(firstCampaignAssets, "resolution")
        : [],
    [firstCampaign, firstCampaignAssets]
  );

  // First campaign assets' resolutions for only jpeg assets
  const firstCampaigndeliveredImageAssetResolutions = useMemo(
    () =>
      firstCampaign && firstCampaign.type === CampaignType.DIGITAL
        ? _.map(
            _.filter(firstCampaignAssets, (asset) =>
              _.includes(["image/jpeg", "image/png"], asset.type)
            ),
            "resolution"
          )
        : [],
    [firstCampaign, firstCampaignAssets]
  );

  // Second campaign assets' resolutions for both types mp4 and jpeg
  const secondCampaignDeliveredAssetResolutions = useMemo(
    () =>
      secondCampaign && secondCampaign.type === CampaignType.DIGITAL
        ? _.map(secondCampaignAssets, "resolution")
        : [],
    [secondCampaign, secondCampaignAssets]
  );

  // First campaign assets' resolutions for only jpeg assets
  const secondCampaigndeliveredImageAssetResolutions = useMemo(
    () =>
      secondCampaign && secondCampaign.type === CampaignType.DIGITAL
        ? _.map(
            _.filter(secondCampaignAssets, (asset) =>
              _.includes(["image/jpeg", "image/png"], asset.type)
            ),
            "resolution"
          )
        : [],
    [secondCampaign, secondCampaignAssets]
  );

  const [checkedCheckboxes, setCheckedCheckboxes] = useState([]);

  const [assetsToMerge, setAssetsToMerge] = useState([]);

  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewModalData, setPreviewModalData] = useState({
    campaign: undefined,
    deliveredAssetResolutions: [],
    deliveredImageAssetResolutions: [],
    assets: [],
    users: undefined,
    agencys: undefined,
    headerText: "",
    icon: "",
  });
  const [previewModalDataForOnSuccess, setPreviewModalDataForOnSuccess] = useState({});

  useEffect(() => {
    dispatch.users.init();
    dispatch.agencys.load(AgencyType.PRINT_SERVICE);
  }, []);

  const users = useSelector(getUsers);
  const agencys = useSelector(getAgencys);
  const activeUser = useSelector(getActiveUser);

  const initialValues = useMemo(
    () =>
      secondCampaign && {
        ...secondCampaign,
        digitalSizes:
          secondCampaign.type === CampaignType.DIGITAL &&
          !secondGroupedCampaign.isLoading &&
          secondCampaign.requiredResolutions.items &&
          secondCampaign.nativeResolutions.items
            ? makeDigitalSizesList(
                secondCampaign.requiredResolutions.items,
                secondCampaign.nativeResolutions.items
              )
            : [],
        printSizes:
          secondCampaign.type === CampaignType.PRINT &&
          !secondGroupedCampaign.isLoading &&
          secondCampaign.printSizes
            ? _.map(secondCampaign.printSizes.items, "printSizeID")
            : [],
      },
    [secondCampaign]
  );
  const combineNotes = (key) => {
    return _.includes(checkedCheckboxes, key)
      ? secondCampaign[key]
        ? secondCampaign[key] + "; " + firstCampaign[key]
        : firstCampaign[key]
      : secondCampaign[key];
  };

  // Do required checks and show preview of the campaign in a modal
  const preSubmit = (values, actions) => {
    let errors;
    // Campaign cannot start after it ends
    if (getAWSDate(values.start_time) > getAWSDate(values.end_time))
      errors = { ...errors, end_time: t("campaign_form_date_error") };
    // Delivery deadline cannot be after start time
    if (
      values.deliveryDeadline &&
      getAWSDate(values.deliveryDeadline) > getAWSDate(values.end_time)
    ) {
      errors = {
        ...errors,
        deliveryDeadline: t("campaign_form_delivery_deadline_end_time_error"),
      };
    }

    if (errors) {
      actions.resetForm({ values, errors });
      return;
    }

    const previewCampaignFields = {
      name: values.name,
      advertiser: values.advertiser,
      contract_number: values.contract_number,
      start_time: values.start_time,
      end_time: values.end_time,
      deliveryDeadline: values.deliveryDeadline,
      status: values.status + ` (${t("status_might_change_message")})`,
      notes: combineNotes("notes"),
      adminNotes: combineNotes("adminNotes"),
      scheduling_notes: combineNotes("scheduling_notes"),
      orderlineID: values.orderlineID,
      salesperson: values.salesperson,
      salesperson_contact: values.salesperson_contact,
      mediaDeliveryPersonContacts: _.includes(checkedCheckboxes, "mediaDeliveryPersonContacts")
        ? _.union(
            secondCampaign.mediaDeliveryPersonContacts || [],
            firstCampaign.mediaDeliveryPersonContacts || []
          )
        : values.mediaDeliveryPersonContacts,
      updatedAt: new Date(),
    };

    let previewCampaign;
    if (values.type === CampaignType.DIGITAL) {
      const requiredResolutions = values.useSourceSizes
        ? firstCampaign.requiredResolutions
        : secondCampaign.requiredResolutions;

      const nativeResolutions = values.useSourceSizes
        ? firstCampaign.nativeResolutions
        : secondCampaign.nativeResolutions;

      previewCampaign = {
        ...secondCampaign,
        ...previewCampaignFields,
        requiredResolutions,
        nativeResolutions,
      };
    } else {
      const printSizes = values.useSourceSizes
        ? firstCampaign.printSizes
        : secondCampaign.printSizes;

      previewCampaign = { ...secondCampaign, ...previewCampaignFields, printSizes };
    }
    const previewAssets = _.concat(secondCampaignAssets, assetsToMerge);
    const previewDeliveredAssetResolutions = _.concat(
      secondCampaignDeliveredAssetResolutions,
      _.map(assetsToMerge, "resolution")
    );
    const previewDeliveredImageAssetResolutions = _.concat(
      secondCampaigndeliveredImageAssetResolutions,
      _.map(
        _.filter(assetsToMerge, (asset) => _.includes(["image/jpeg", "image/png"], asset.type)),
        "resolution"
      )
    );

    setPreviewModalData({
      campaign: previewCampaign,
      deliveredAssetResolutions: previewDeliveredAssetResolutions,
      deliveredImageAssetResolutions: previewDeliveredImageAssetResolutions,
      assets: previewAssets,
      users: users.items,
      agencys: agencys.items,
      headerText: t("merge_campaigns"),
      icon: "edit outline",
    });
    setPreviewModalDataForOnSuccess({ values });
    setPreviewModalOpen(true);
  };

  const submit = async ({ values }) => {
    try {
      const updateProps = {
        name: values.name,
        advertiser: values.advertiser,
        contract_number: values.contract_number,
        start_time: values.start_time,
        end_time: values.end_time,
        deliveryDeadline: values.deliveryDeadline,
        notes: combineNotes("notes"),
        adminNotes: combineNotes("adminNotes"),
        scheduling_notes: combineNotes("scheduling_notes"),
        orderlineID: values.orderlineID,
        salesperson: values.salesperson,
        salesperson_contact: values.salesperson_contact,
        mediaDeliveryPersonContacts: _.includes(checkedCheckboxes, "mediaDeliveryPersonContacts")
          ? _.union(
              secondCampaign.mediaDeliveryPersonContacts || [],
              firstCampaign.mediaDeliveryPersonContacts || []
            )
          : values.mediaDeliveryPersonContacts,
        collaborators: _.union(
          firstCampaign.collaborators || [],
          secondCampaign.collaborators || [],
          // Add owner to destination campaign's collaborators so if the first campaign's creator is a contributor, they have access to the destination campaign
          [firstCampaign.owner]
        ),
        viewers: _.union(firstCampaign.viewers || [], secondCampaign.viewers || []),
      };

      // Digital campaign
      if (values.type === CampaignType.DIGITAL) {
        // Flatten digital sizes to separate lists for required resolutions and native resolutions
        const requiredResolutions = _.map(values.digitalSizes, "requiredResolution");
        const nativeResolutions = _.flatten(_.map(values.digitalSizes, "nativeResolutions"));

        const campaignUpdateResolutionLists = getCampaignUpdateResolutionLists(
          secondCampaign.requiredResolutions.items,
          secondCampaign.nativeResolutions.items,
          requiredResolutions,
          nativeResolutions
        );

        // Update the second campaign
        const updatedCampaign = await dispatch.campaigns.update({
          updateProps: { id: secondCampaign.id, ...updateProps },
          ...campaignUpdateResolutionLists,
        });

        // Move selected assets to the destination campaign
        for (const asset of assetsToMerge) {
          await dispatch.assets.update({
            updateProps: {
              id: asset.id,
              campaignID: secondCampaign.id,
              mediaCollaborators: secondCampaign.mediaCollaborators,
            },
          });
        }

        // Delete the first campaign
        await dispatch.campaigns.delete(firstCampaign);

        const newAssets = _.concat(secondCampaignAssets, assetsToMerge);
        const assetActions = _.map(newAssets, (asset) => ({
          type: "runQC",
          props: { ...asset, campaignID: secondCampaign.id },
        }));

        // Only run QC if result campaign has any assets
        if (assetActions.length > 0) {
          toast(undefined, t("merge_campaigns_success_with_qc"));
          onSuccessRunQC({ assetActions, campaign: secondCampaign });
        } else {
          toast(undefined, t("merge_campaigns_digital_success"));
          onSuccessRedirect(updatedCampaign);
        }

        // Print campaign
      } else {
        let printUpdateProps = { ...updateProps };

        // Move selected assets to the destination campaign
        for (const asset of assetsToMerge) {
          await dispatch.assets.update({
            updateProps: {
              id: asset.id,
              campaignID: secondCampaign.id,
              mediaCollaborators: secondCampaign.mediaCollaborators,
            },
          });
        }

        // Update print campaign's status from AWAITING DELIVERY or COPY MISSING to DELIVERED if any assets were merged
        if (
          (secondCampaign.status === CampaignStatus.AWAITING_DELIVERY ||
            secondCampaign.status === CampaignStatus.COPY_MISSING) &&
          assetsToMerge.length > 0
        ) {
          printUpdateProps = {
            ...printUpdateProps,
            status: CampaignStatus.DELIVERED,
            automaticStatus: CampaignStatus.DELIVERED,
          };
        }

        const campaignUpdatePrintSizeChanges = getCampaignUpdatePrintSizeChanges(
          secondCampaign.printSizes.items,
          values.printSizes
        );

        const updatedCampaign = await dispatch.campaigns.update({
          updateProps: { id: secondCampaign.id, ...printUpdateProps },
          ...campaignUpdatePrintSizeChanges,
        });

        // Delete the first campaign
        await dispatch.campaigns.delete(firstCampaign);

        toast(undefined, t("merge_campaigns_print_success"));
        onSuccessRedirect(updatedCampaign);
      }
    } catch (error) {
      console.error(error);
      toast("error", t("merge_campaigns_failure"));
    }
  };

  const onSwitchSides = () => {
    // Set both campaigns' assets as not selected
    const newFirstCampaignAssets = _.map(secondCampaignAssets, (asset) => ({
      ...asset,
      isSelected: false,
    }));
    const newSecondCampaignAssets = _.map(firstCampaignAssets, (asset) => ({
      ...asset,
      isSelected: false,
    }));

    setFirstCampaignAndAssets({ ...secondCampaignAndAssets, assets: newFirstCampaignAssets });
    setSecondCampaignAndAssets({ ...firstCampaignAndAssets, assets: newSecondCampaignAssets });
    setAssetsToMerge([]);
    setCheckedCheckboxes([]);
  };

  const handleCheckboxSelected = (key, checked) => {
    const newCheckedCheckboxes = checked
      ? _.concat(checkedCheckboxes, key)
      : _.without(checkedCheckboxes, key);
    setCheckedCheckboxes(newCheckedCheckboxes);
  };

  const handleAssetSelected = (selectedAsset) => {
    // Add / remove asset from assets to merge
    const updatedAssets = _.map(firstCampaignAssets, (asset) =>
      asset.id === selectedAsset ? { ...asset, isSelected: !asset.isSelected } : asset
    );
    const newAssetsToMerge = updatedAssets.filter((currentAsset) => currentAsset.isSelected);
    setAssetsToMerge(newAssetsToMerge);
    setFirstCampaignAndAssets({ ...firstCampaignAndAssets, assets: updatedAssets });
  };

  const MergeCampaignRow = ({
    firstData,
    secondData,
    campaignKey,
    onChange,
    orderlineOptions,
    selectedOrderline,
    disableSelect,
  }) => {
    const { t } = useTranslation("common");

    const iconName = _.includes(CAMPAIGN_FIELDS_TO_COMBINE, campaignKey)
      ? "plus"
      : "long arrow alternate right";
    const iconSize = _.includes(CAMPAIGN_FIELDS_TO_COMBINE, campaignKey) ? "large" : "big";

    const visibleOrderlineOption = orderlineOptions
      ? orderlineOptions.filter((orderlineOption) => !orderlineOption.hidden)
      : [];

    return (
      <Grid.Row key={campaignKey}>
        <Grid.Column width={7}>
          <Grid columns={2}>
            <Grid.Column>{t("campaign_" + campaignKey)}</Grid.Column>
            <Grid.Column>{firstData}</Grid.Column>
          </Grid>
        </Grid.Column>
        <Grid.Column
          width={2}
          style={{
            display: "flex",
            justifyContent: "center",
          }}
        >
          {campaignKey === "orderlineID" ? (
            <div>
              <Dropdown
                disabled={disableSelect}
                style={{ width: "10rem" }}
                compact
                selection
                options={visibleOrderlineOption}
                onChange={(e, props) => {
                  onChange(props);
                }}
                value={selectedOrderline}
              />
            </div>
          ) : (
            <>
              <Checkbox
                disabled={!firstData}
                checked={_.includes(checkedCheckboxes, campaignKey)}
                onChange={(_e, props) => onChange(props)}
                style={{
                  paddingTop: `${
                    _.includes(CAMPAIGN_FIELDS_TO_COMBINE, campaignKey) ? "2px" : "4px"
                  }`,
                }}
              />
              <Icon name={iconName} size={iconSize} />
            </>
          )}
        </Grid.Column>
        <Grid.Column width={7}>
          <Grid columns={2}>
            <Grid.Column>{t("campaign_" + campaignKey)}</Grid.Column>
            <Grid.Column>{secondData}</Grid.Column>
          </Grid>
        </Grid.Column>
      </Grid.Row>
    );
  };

  return firstCampaign && secondCampaign && initialValues ? (
    <LoadResolver
      isLoading={
        users.isLoading ||
        agencys.isLoading ||
        firstGroupedCampaign.isLoading ||
        secondGroupedCampaign.isLoading
      }
      error={users.error || agencys.error}
      hideWhenLoading
    >
      <Formik
        initialValues={initialValues}
        // Enables form to be reinitialized when initialValues change after using the switch sides -button
        enableReinitialize
        onSubmit={preSubmit}
      >
        {({ submitCount, errors, isSubmitting, setFieldValue, setSubmitting }) => (
          <FormikForm>
            {campaignHasProcessedAssets() && (
              <Message>
                <Message.Header>{t("merge_campaigns_error")}</Message.Header>
              </Message>
            )}
            {<h2>{`Merge ${initialValues.type.toLowerCase()} campaigns`}</h2>}
            <Divider />
            {/* Grid for merging campaign info */}
            <Grid columns={3} relaxed divided="vertically" className="merge-campaign-grid">
              <Grid.Row verticalAlign="middle">
                <Grid.Column width={7} textAlign="center">
                  <h2>{t("source_campaign")}</h2>
                </Grid.Column>
                <Grid.Column width={2}>
                  <Button
                    type="button"
                    content={t("merge_campaigns_switch_sides")}
                    icon="exchange"
                    labelPosition="right"
                    onClick={onSwitchSides}
                  />
                </Grid.Column>
                <Grid.Column width={7} textAlign="center">
                  <h2>{t("destination_campaign")}</h2>
                </Grid.Column>
              </Grid.Row>
              {CAMPAIGN_KEYS.map((key) =>
                // Print sizes
                key === "printSizes" && firstCampaign[key] ? (
                  firstCampaign["type"] && firstCampaign["type"] === "PRINT" ? (
                    <MergeCampaignRow
                      firstData={formatPrintSizes(firstCampaign.printSizes.items, t)}
                      secondData={formatPrintSizes(secondCampaign.printSizes.items, t)}
                      campaignKey={key}
                      key={key}
                      onChange={({ checked }) => {
                        // New print sizes list with the print sizes from...
                        let newPrintSizes;
                        // ...the first campaign
                        if (checked) {
                          newPrintSizes = _.map(firstCampaign.printSizes.items, "printSizeID");
                          setFieldValue("useSourceSizes", true);

                          // ...or the second campaign i.e. initial values
                        } else {
                          newPrintSizes = initialValues.printSizes;
                          setFieldValue("useSourceSizes", false);
                        }
                        setFieldValue(key, newPrintSizes);
                        handleCheckboxSelected(key, checked);
                      }}
                    />
                  ) : null
                ) : // Digital sizes
                key === "requiredResolutions" && firstCampaign[key] ? (
                  firstCampaign["type"] && firstCampaign["type"] === "DIGITAL" ? (
                    <MergeCampaignRow
                      firstData={formatResolutions(
                        firstCampaign[key],
                        firstCampaign["nativeResolutions"],
                        activeUser ? activeUser.item : undefined,
                        t,
                        firstCampaignDeliveredAssetResolutions,
                        firstCampaigndeliveredImageAssetResolutions
                      )}
                      secondData={formatResolutions(
                        secondCampaign[key],
                        secondCampaign["nativeResolutions"],
                        activeUser ? activeUser.item : undefined,
                        t,
                        secondCampaignDeliveredAssetResolutions,
                        secondCampaigndeliveredImageAssetResolutions
                      )}
                      campaignKey={key}
                      key={key}
                      onChange={({ checked }) => {
                        // New digital sizes list with the resolutions from...
                        let newDigitalSizes;
                        // ...the first campaign
                        if (checked) {
                          newDigitalSizes = makeDigitalSizesList(
                            firstCampaign.requiredResolutions.items,
                            firstCampaign.nativeResolutions.items
                          );
                          setFieldValue("useSourceSizes", true);
                          // ...or the second campaign i.e. initial values
                        } else {
                          newDigitalSizes = initialValues.digitalSizes;
                          setFieldValue("useSourceSizes", false);
                        }
                        setFieldValue("digitalSizes", newDigitalSizes);
                        handleCheckboxSelected(key, checked);
                      }}
                    />
                  ) : null
                ) : // Rest of the keys
                firstCampaign[key] || secondCampaign[key] ? (
                  <MergeCampaignRow
                    firstData={
                      // Format dates
                      key === "end_time" || key === "start_time" || key === "deliveryDeadline"
                        ? formatDate(firstCampaign[key])
                        : // Format media delivery person contacts array
                        key === "mediaDeliveryPersonContacts"
                        ? formatMediaDeliveryPersonContacts(firstCampaign[key])
                        : // Rest of the keys don't need formatting
                          firstCampaign[key]
                    }
                    secondData={
                      // Format dates
                      key === "end_time" || key === "start_time" || key === "deliveryDeadline"
                        ? formatDate(secondCampaign[key])
                        : // Format media delivery person contacts array
                        key === "mediaDeliveryPersonContacts"
                        ? formatMediaDeliveryPersonContacts(secondCampaign[key])
                        : // Rest of the keys don't need formatting
                          secondCampaign[key]
                    }
                    campaignKey={key}
                    key={key}
                    disableSelect={
                      firstCampaign["orderlineID"] === "" && secondCampaign["orderlineID"] === ""
                    }
                    orderlineOptions={[
                      {
                        key: "keep-left",
                        value: firstCampaign["orderlineID"] ?? "",
                        text: t("use left"),
                        hidden: firstCampaign["orderlineID"] === "",
                      },
                      {
                        key: "keep-right",
                        value: secondCampaign["orderlineID"] ?? "",
                        text: t("use right"),
                        hidden: secondCampaign["orderlineID"] === "",
                      },
                      {
                        key: "combine",
                        value: `${firstCampaign["orderlineID"]}_${secondCampaign["orderlineID"]}`,
                        text: t("combine"),
                        hidden:
                          secondCampaign["orderlineID"] === "" ||
                          firstCampaign["orderlineID"] === "",
                      },
                      {
                        key: "use-blank",
                        value: "empty",
                        text: t("use blank"),
                        hidden: firstCampaign["orderlineID"] !== "",
                      },
                    ]}
                    selectedOrderline={values.orderlineID}
                    onChange={(props) => {
                      if (key === "orderlineID") {
                        const { value } = props;
                        if (value === "empty") {
                          setFieldValue("orderlineID", "");
                        } else {
                          setFieldValue("orderlineID", value);
                        }
                      } else {
                        const { checked } = props;
                        checked
                          ? setFieldValue(key, firstCampaign[key] || null)
                          : setFieldValue(key, secondCampaign[key] || null);
                        handleCheckboxSelected(key, checked);
                      }
                    }}
                  />
                ) : null
              )}
            </Grid>
            <Divider />
            {/* Merge campaign assets */}
            <Grid columns={2}>
              <Grid.Row divided>
                <Grid.Column width={8} className="modal">
                  <h3>{t("merge_campaigns_select_assets")}:</h3>
                  <AssetSelectionCarousel
                    assets={firstCampaignAssets}
                    maxNumberOfAssets={4}
                    handleAssetSelectionOnClick={handleAssetSelected}
                    handleSetClickedAssetId={handleAssetSelected} // Prop required by component but not needed
                    allowReselectAsset
                    renderFromModal
                  />
                </Grid.Column>
                <Grid.Column width={8} className="modal destination-campaign">
                  <h3>{t("merge_campaigns_destination_assets")}:</h3>
                  <AssetSelectionCarousel
                    assets={secondCampaignAssets}
                    maxNumberOfAssets={4}
                    handleAssetSelectionOnClick={() => {}} // Prop required by component but not needed
                    handleSetClickedAssetId={() => {}} // Prop required by component but not needed
                    renderFromModal
                  />
                </Grid.Column>
              </Grid.Row>
            </Grid>
            <ErrorInfo errors={errors} submitCount={submitCount} t={t} tPrefix={"campaign_"} />
            <div className="form-footer">
              {goPrev && (
                <Button type="button" primary onClick={goPrev}>
                  {t("back")}
                </Button>
              )}
              <Button
                primary
                type="submit"
                loading={isSubmitting}
                disabled={campaignHasProcessedAssets()}
              >
                {t("submit")}
              </Button>
            </div>
            <MergeCampaignPreviewModal
              data={previewModalData}
              open={previewModalOpen}
              setOpen={setPreviewModalOpen}
              onSuccess={submit}
              onCancel={() => setSubmitting(false)}
              dataForOnSuccess={previewModalDataForOnSuccess}
              activeUser={activeUser.item}
              t={t}
            />
          </FormikForm>
        )}
      </Formik>
    </LoadResolver>
  ) : (
    <LoadResolver isLoading={true} />
  );
};
